import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button, Form, Modal, Spinner, Badge } from 'react-bootstrap';
import { FaImage, FaPlus, FaTrash, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { SecureImage, SecureVideo } from './SecureMedia';
import { API_BASE } from '../config';

const CATEGORIES = [
  "Electronics", "Appliances", "Furniture", "Kitchenware", "Bedding & Linens",
  "Clothing & Accessories", "Jewellery & Watches", "Art & Decor", "Tools & Hardware",
  "Sports & Recreation", "Books & Leisure", "Media, Toys & Games",
  "Musical Instruments", "Health & Personal Care", "Miscellaneous", "Holiday & Costumes",
];

const getBoxStyles = (box, rot) => {
  if (!box || box.length !== 4) return {};
  const [ymin, xmin, ymax, xmax] = box.map(v => v / 10);
  const width = xmax - xmin;
  const height = ymax - ymin;
  switch (rot) {
    case 90:  return { top: `${xmin}%`,       left: `${100 - ymax}%`, width: `${height}%`, height: `${width}%` };
    case 180: return { top: `${100 - ymax}%`, left: `${100 - xmax}%`, width: `${width}%`,  height: `${height}%` };
    case 270: return { top: `${100 - xmax}%`, left: `${ymin}%`,       width: `${height}%`, height: `${width}%` };
    default:  return { top: `${ymin}%`,       left: `${xmin}%`,       width: `${width}%`,  height: `${height}%` };
  }
};

const conditionBadge = (cond) => {
  const map = { New: 'primary', Excellent: 'success', Good: 'info', Fair: 'warning' };
  return <Badge bg={map[cond] || 'secondary'}>{cond}</Badge>;
};

export default function ItemDetailModal({
  show,
  item,
  onItemChange,
  onHide,
  onSaveComplete,
  onDelete,
  onNavigate,
  canNavigatePrev,
  canNavigateNext,
  roomList,
  containerList,
  t,
  currency,
  location,
  authenticatedFetch,
}) {
  const [isEditing, setIsEditing]     = useState(false);
  const [rotation, setRotation]       = useState(0);
  const [boxRotation, setBoxRotation] = useState(0);
  const [attaching, setAttaching]     = useState(false);
  const [refreshing, setRefreshing]   = useState(false);
  const [saving, setSaving]           = useState(false);
  const attachmentInputRef            = useRef(null);
  const stayInEditRef                 = useRef(false);

  // Reset UI state when a different item is shown, unless navigating from edit mode
  useEffect(() => {
    if (!stayInEditRef.current) setIsEditing(false);
    stayInEditRef.current = false;
    setRotation(0);
    setBoxRotation(0);
  }, [item?.id]);

  if (!item) return null;

  const filename = item.source_file?.split('/').pop();
  const fileUrl  = filename ? `${API_BASE}/media/${filename}` : null;
  const isVideo  = item.source_file?.match(/\.(mp4|mov|avi)$/i);

  const handleFieldChange = (field, value) => onItemChange(prev => ({ ...prev, [field]: value }));

  // Core save — does not toggle edit mode, so it can be reused by both handleSave and handleNavigateFromEdit
  const saveItem = async () => {
    const res = await authenticatedFetch(`${API_BASE}/inventory/update`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify([item]),
    });
    if (!res.ok) throw new Error('Save failed');
    await onSaveComplete();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveItem();
      setIsEditing(false);
      alert(t.updateSuccess);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  // Save current item then navigate — stays in edit mode
  const handleNavigateFromEdit = async (direction) => {
    setSaving(true);
    try {
      await saveItem();
      stayInEditRef.current = true;
      onNavigate(direction);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleRefreshAI = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/refresh-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item_id: item.id }),
      });
      if (!res.ok) throw new Error('Refresh failed');
      const updated = await res.json();
      onItemChange(prev => ({ ...prev, ...updated }));
      await onSaveComplete();
      alert(t.refreshSuccess);
    } catch (err) { alert(err.message); }
    finally { setRefreshing(false); }
  };

  const handleAttachMedia = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setAttaching(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/${item.id}/attach`, { method: 'POST', body: formData });
      if (res.ok) {
        await onSaveComplete();
        const itemRes = await authenticatedFetch(`${API_BASE}/inventory/${item.id}`);
        if (itemRes.ok) onItemChange({ ...(await itemRes.json()), viewingOriginal: true });
        alert(t.mediaAttachedSuccess);
      } else {
        let msg = `Upload failed (HTTP ${res.status})`;
        try { msg = (await res.json())?.detail || msg; } catch (_) {}
        alert(msg);
      }
    } catch (err) { alert(err.message); }
    finally { setAttaching(false); if (attachmentInputRef.current) attachmentInputRef.current.value = ''; }
  };

  const handleSetMainMedia = async (attachmentId) => {
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/${item.id}/set-main-media`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attachment_id: attachmentId }),
      });
      if (res.ok) {
        await onSaveComplete();
        const itemRes = await authenticatedFetch(`${API_BASE}/inventory/${item.id}`);
        if (itemRes.ok) onItemChange({ ...(await itemRes.json()), viewingOriginal: true });
        alert(t.primaryPhotoUpdated);
      }
    } catch (err) { alert(err.message); }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm(t.confirmDeleteAttachment)) return;
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/attachments/${attachmentId}`, { method: 'DELETE' });
      if (res.ok) {
        await onSaveComplete();
        const itemRes = await authenticatedFetch(`${API_BASE}/inventory/${item.id}`);
        if (itemRes.ok) onItemChange({ ...(await itemRes.json()), viewingOriginal: true });
      }
    } catch (err) { alert(err.message); }
  };

  const googleSearchUrl = item.item
    ? `https://www.google.com/search?q=${encodeURIComponent('buy ' + location + ' ' + item.item + ' new')}`
    : '#';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? t.editList : item.item}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          {/* ── Left: media viewer + evidence gallery ── */}
          <Col md={6} className="mb-3 d-flex flex-column align-items-center">
            <div className="media-container rounded overflow-hidden border bg-dark d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
              <input type="file" onChange={handleAttachMedia} style={{ display: 'none' }} ref={attachmentInputRef} />
              {fileUrl ? (
                <div style={{ position: 'relative', display: 'inline-block', width: '100%', transform: `rotate(${rotation}deg)`, transition: 'transform 0.3s ease' }}>
                  {isVideo
                    ? <SecureVideo src={fileUrl} controls style={{ maxWidth: '100%', maxHeight: '400px', display: 'block' }} />
                    : <SecureImage src={fileUrl} alt="Item" style={{ maxWidth: '100%', maxHeight: '400px', display: 'block' }} />
                  }
                  {(item.viewingOriginal && item.box_2d) && (
                    <div className="bounding-box" style={getBoxStyles(item.box_2d, boxRotation)} />
                  )}
                </div>
              ) : (
                <div className="text-white text-center"><FaImage size={50} /><br />{t.noMediaAvailable}</div>
              )}
            </div>

            {/* Evidence gallery */}
            <div className="mt-3 p-2 bg-body-tertiary rounded border w-100">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="small fw-bold text-muted text-uppercase mb-0">{t.evidenceGallery}</h6>
                <Button variant="outline-primary" size="sm" onClick={() => attachmentInputRef.current.click()} disabled={attaching}>
                  <FaPlus /> {t.addMedia}
                </Button>
              </div>
              <div className="d-flex gap-2 overflow-auto pb-2">
                <div
                  className={`border rounded overflow-hidden position-relative ${item.viewingOriginal ? 'border-primary border-3' : 'opacity-75'}`}
                  style={{ width: '60px', height: '60px', flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => onItemChange(prev => ({ ...prev, viewingOriginal: true }))}
                >
                  <SecureImage
                    src={item.thumbnail_file ? `${API_BASE}/${item.thumbnail_file}` : `${API_BASE}/media/${item.source_file?.split('/').pop()}`}
                    alt="primary"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div className="position-absolute top-0 start-0 bg-primary text-white px-1" style={{ fontSize: '0.5rem' }}>{t.mainBadge}</div>
                </div>
                {item.attachments?.map(att => (
                  <div
                    key={att.id}
                    className="border rounded overflow-hidden position-relative"
                    style={{ width: '60px', height: '60px', flexShrink: 0, cursor: 'pointer' }}
                    onClick={() => onItemChange(prev => ({ ...prev, source_file: att.file_path, viewingOriginal: false }))}
                  >
                    <SecureImage
                      src={att.thumbnail_path ? `${API_BASE}/${att.thumbnail_path}` : `${API_BASE}/${att.file_path}`}
                      alt="att"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-between p-1 opacity-0 hover-opacity-100 bg-dark bg-opacity-50">
                      <Button variant="success" size="sm" style={{ fontSize: '0.4rem' }} onClick={(e) => { e.stopPropagation(); handleSetMainMedia(att.id); }}>{t.mainBadge}</Button>
                      <Button variant="danger"  size="sm" style={{ fontSize: '0.4rem' }} onClick={(e) => { e.stopPropagation(); handleDeleteAttachment(att.id); }}><FaTrash /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          {/* ── Right: item details / edit form ── */}
          <Col md={6}>
            {isEditing ? (
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label>{t.item}</Form.Label>
                  <Form.Control type="text" value={item.item || ''} onChange={(e) => handleFieldChange('item', e.target.value)} />
                </Form.Group>

                <Row className="mb-2">
                  <Col>
                    <Form.Label>{t.cost} ({currency})</Form.Label>
                    <Form.Control type="number" value={item.cost_cad ?? ''} onChange={(e) => handleFieldChange('cost_cad', e.target.value)} />
                  </Col>
                  <Col>
                    <Form.Label>{t.qty}</Form.Label>
                    <Form.Control type="number" value={item.quantity ?? ''} onChange={(e) => handleFieldChange('quantity', e.target.value)} />
                  </Col>
                </Row>

                <Form.Group className="mb-2">
                  <Form.Label>{t.type || 'Category'}</Form.Label>
                  <Form.Select value={item.type || ''} onChange={(e) => handleFieldChange('type', e.target.value)}>
                    <option value="">{t.selectCategory}</option>
                    {CATEGORIES.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
                  </Form.Select>
                </Form.Group>

                <Row className="mb-2">
                  <Col>
                    <Form.Label>{t.room || 'Room'}</Form.Label>
                    <Form.Select value={item.room || ''} onChange={(e) => handleFieldChange('room', e.target.value)}>
                      <option value="">{t.selectRoom}</option>
                      {(roomList || []).map((r, i) => <option key={i} value={r}>{r}</option>)}
                    </Form.Select>
                  </Col>
                  <Col>
                    <Form.Label>{t.storage || 'Storage'}</Form.Label>
                    <Form.Select value={item.container || ''} onChange={(e) => handleFieldChange('container', e.target.value)}>
                      <option value="">{t.selectStorage}</option>
                      {(containerList || []).map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col>
                    <Form.Label>{t.yearPurchased || 'Year'}</Form.Label>
                    <Form.Control type="number" value={item.year_purchased ?? ''} onChange={(e) => handleFieldChange('year_purchased', e.target.value)} />
                  </Col>
                  <Col>
                    <Form.Label>{t.cond || 'Condition'}</Form.Label>
                    <Form.Select value={item.condition || ''} onChange={(e) => handleFieldChange('condition', e.target.value)}>
                      <option value="New">New</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">{t.condGood || 'Good'}</option>
                      <option value="Fair">{t.condFair || 'Fair'}</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Form.Group className="mb-2">
                  <Form.Label>{t.replacementLink || 'Replacement Link'}</Form.Label>
                  <Form.Control type="url" value={item.replacement_link || ''} onChange={(e) => handleFieldChange('replacement_link', e.target.value)} />
                </Form.Group>
              </Form>
            ) : (
              <div>
                <h5 className="text-success fw-bold mb-1">${Number(item.cost_cad || 0).toLocaleString()} {currency}</h5>
                <div className="mb-2 d-flex flex-wrap gap-2 align-items-center">
                  {conditionBadge(item.condition)}
                  <span className="text-muted small"><strong>{t.qty}:</strong> {item.quantity}</span>
                </div>
                <div className="mb-2 small text-muted">
                  {item.type          && <div><strong>{t.type          || 'Category'}:</strong> {item.type}</div>}
                  {item.room          && <div><strong>{t.room          || 'Room'}:</strong>     {item.room}</div>}
                  {item.container     && <div><strong>{t.storage       || 'Storage'}:</strong>  {item.container}</div>}
                  {item.year_purchased && <div><strong>{t.yearPurchased || 'Year'}:</strong>    {item.year_purchased}</div>}
                </div>
                <div className="mb-3 d-flex flex-wrap gap-2">
                  {item.replacement_link && (
                    <Button variant="outline-info" size="sm" href={item.replacement_link} target="_blank">{t.viewReplacement}</Button>
                  )}
                  <Button variant="outline-secondary" size="sm" href={googleSearchUrl} target="_blank">{t.searchGoogle}</Button>
                  <Button variant="outline-primary"   size="sm" onClick={handleRefreshAI} disabled={refreshing}>
                    {refreshing ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : null}
                    {t.refreshAiData}
                  </Button>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        {/* Delete always far-left */}
        <Button variant="danger" className="me-auto" onClick={() => onDelete(item.id)}>{t.delete}</Button>

        {isEditing ? (
          <>
            {/* Prev/Next in edit mode — auto-saves before navigating */}
            <Button variant="outline-secondary" size="sm" onClick={() => handleNavigateFromEdit(-1)} disabled={!canNavigatePrev || saving}>
              <FaChevronLeft />
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => handleNavigateFromEdit(1)}  disabled={!canNavigateNext || saving}>
              <FaChevronRight />
            </Button>
            <Button variant="success" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : null}
              {t.save}
            </Button>
            <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={saving}>{t.cancel}</Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" size="sm" onClick={() => onNavigate(-1)} disabled={!canNavigatePrev}><FaChevronLeft /></Button>
            <Button variant="outline-secondary" size="sm" onClick={() => onNavigate(1)}  disabled={!canNavigateNext}><FaChevronRight /></Button>
            <Button variant="primary"           onClick={() => setIsEditing(true)}>{t.editList}</Button>
            <Button variant="outline-secondary" onClick={onHide}>{t.close}</Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
